import { FC, MouseEvent, ReactNode } from "react";
import { Icon } from "../icon/icon";
import { LoadingOval } from "../loaders/loaders";

export type ButtonProps = {
  text: string | ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  type: "button" | "submit";
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
  iconClassName?: string;
  variation?:
    | "primary"
    | "plain"
    | "plain-grey"
    | "plain-danger"
    | "grey"
    | "danger"
    | "green-outline"
    | "white"
    | "bland";
  loaderWidth?: string;
  loaderHeight?: string;
  iconDimension?: number;
  extra?: ReactNode;
};

export const Button: FC<ButtonProps> = ({
  text,
  onClick,
  type = "submit",
  className = "",
  loading = false,
  disabled = false,
  icon = "",
  iconClassName = "",
  variation = "primary",
  loaderWidth = "25",
  loaderHeight = "25",
  iconDimension = 16,
  extra,
}) => {
  const getColors = () => {
    switch (variation) {
      case "primary":
        return "bg-primary border-primary text-white";
      case "plain":
        return "bg-transparent border-primary text-primary";
      case "plain-grey":
        return "bg-transparent border-grey-950/10 text-primary";
      case "plain-danger":
        return "bg-transparent border-grey-950/10 text-danger";
      case "grey":
        return "bg-grey-50 border-grey-50 text-primary";
      case "danger":
        return "bg-danger border-danger text-white";
      case "green-outline":
        return "bg-transparent border-success text-success";
      case "white":
        return "bg-white border-white text-primary";
      case "bland":
        return "bg-transparent border-transparent text-grey-950";
      default:
        break;
    }
  };

  const getLoaderColors = () => {
    switch (variation) {
      case "primary":
        return "#ffffff";
      case "plain":
        return "#04000A";
      default:
        return "#04000A";
    }
  };

  return (
    <button
      type={type}
      onClick={(e) => {
        if (onClick) {
          if (type !== "submit") {
            e.preventDefault();
          }
          onClick(e);
        }
      }}
      disabled={disabled || loading}
      className={` ${getColors()} soft-shrink relative flex w-full items-center justify-center gap-[6px] rounded-[100px] border px-5 py-3 text-base font-bold disabled:cursor-not-allowed disabled:opacity-100 disabled:hover:scale-100 ${loading && "!text-transparent"} ${className} `}
    >
      {icon && (
        <Icon
          svg={icon}
          width={iconDimension}
          height={iconDimension}
          className={iconClassName}
        />
      )}
      {text}
      {extra ?? extra}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingOval
            loaderHeight={loaderHeight}
            loaderWidth={loaderWidth}
            color={getLoaderColors()}
          />
        </div>
      )}
    </button>
  );
};
